
import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const AudioWaveform = ({ audioStream }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);

    // We use this to store the "history" of volume levels for the scrolling effect
    const amplitudeDataRef = useRef([]);

    useEffect(() => {
        if (!audioStream) return;

        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        const audioContext = audioContextRef.current;
        if (audioContext.state === 'suspended') {
            audioContext.resume();
        }

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024; // Larger window for smoother time-domain data
        analyser.smoothingTimeConstant = 0.3; // Some hardware smoothing
        analyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(audioStream);
        source.connect(analyser);
        sourceRef.current = source;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const canvas = canvasRef.current;
        const canvasCtx = canvas.getContext('2d');

        // Initialize amplitude history if empty
        // We want enough bars to fill the screen width. 
        // Let's assume a max of ~50 bars for a clean look.
        const maxBars = 60;
        if (amplitudeDataRef.current.length === 0) {
            amplitudeDataRef.current = new Array(maxBars).fill(0);
        }

        const draw = () => {
            if (!canvas) return;
            animationRef.current = requestAnimationFrame(draw);

            const WIDTH = canvas.width;
            const HEIGHT = canvas.height;

            // Calculate current instantaneous volume (RMS)
            analyser.getByteTimeDomainData(dataArray);
            let sum = 0;
            // We process the time-domain data (waveform) to find the "loudness"
            // dataArray values range 0-255, with 128 being silence (center).
            for (let i = 0; i < dataArray.length; i++) {
                const x = (dataArray[i] - 128) / 128.0;
                sum += x * x;
            }
            const rms = Math.sqrt(sum / dataArray.length);

            // Enhance reactivity: Scale it up a bit so normal speech looks good
            // Dampen the falloff slightly for smoothness? 
            // Actually, for scrolling waveform, instantaneous is usually fine, 
            // but let's add a tiny bit of attack/release if needed. 
            // For now, raw RMS * amplification.
            const amplification = 3.5;
            let value = rms * amplification;
            if (value > 1.0) value = 1.0;

            // Shift history and add new value
            amplitudeDataRef.current.push(value);
            if (amplitudeDataRef.current.length > maxBars) {
                amplitudeDataRef.current.shift();
            }

            // CLEAR
            canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);

            // Bar settings
            // We want distinct bars with rounded caps.
            const gap = 4; // Space between bars
            // Calculate bar width based on canvas width to fit 'maxBars' perfectly?
            // Or fixed spacing? 
            // Fixed spacing looks more consistent as window resizes.
            // Let's say we always draw 'maxBars' spread across the width.
            const totalAvailableWidth = WIDTH - (gap * (maxBars - 1));
            const barWidth = Math.max(3, totalAvailableWidth / maxBars);

            const centerY = HEIGHT / 2;

            // Draw Loop
            for (let i = 0; i < amplitudeDataRef.current.length; i++) {
                const amp = amplitudeDataRef.current[i];

                // Min height so it's not invisible (e.g. 4px dot)
                // Max height is full canvas height
                let barHeight = Math.max(barWidth, (amp * HEIGHT));

                // Smooth scaling: if volume is super low, keep it tiny
                // If it hits edges, maybe clamp with some padding
                if (barHeight > HEIGHT - 4) barHeight = HEIGHT - 4;

                const x = i * (barWidth + gap);
                const y = centerY - (barHeight / 2);

                // Draw Rounded Rectangle
                canvasCtx.fillStyle = '#75a84b'; // Theme Green

                // Opacity: Fade out the oldest bars on the left?
                // WhatsApp doesn't really fade, it just scrolls.
                // But let's add a subtle opacity gradient for "Aesthetic" points
                // The newest bars (right side) are fully opaque.
                // The oldest bars (left side) could be slightly faded.
                const opacity = 0.5 + (0.5 * (i / maxBars));
                canvasCtx.globalAlpha = opacity;

                beginPathRoundedRect(canvasCtx, x, y, barWidth, barHeight, barWidth / 2);
                canvasCtx.fill();
            }
            canvasCtx.globalAlpha = 1.0;
        };

        // Helper for rounded rect compatible with older browsers just in case
        const beginPathRoundedRect = (ctx, x, y, width, height, radius) => {
            if (width < 2 * radius) radius = width / 2;
            if (height < 2 * radius) radius = height / 2;
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.arcTo(x + width, y, x + width, y + height, radius);
            ctx.arcTo(x + width, y + height, x, y + height, radius);
            ctx.arcTo(x, y + height, x, y, radius);
            ctx.arcTo(x, y, x + width, y, radius);
            ctx.closePath();
        };

        draw();

        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            if (sourceRef.current) sourceRef.current.disconnect();
        };
    }, [audioStream]);

    // Handle Resize
    useEffect(() => {
        const handleResize = () => {
            if (canvasRef.current && canvasRef.current.parentElement) {
                // Increase resolution for retina displays
                const dpr = window.devicePixelRatio || 1;
                const rect = canvasRef.current.parentElement.getBoundingClientRect();

                canvasRef.current.width = rect.width * dpr;
                canvasRef.current.height = (rect.height || 56) * dpr;

                // Scale context to match
                const ctx = canvasRef.current.getContext('2d');
                ctx.scale(dpr, dpr);

                // We need to adjust our drawing logic to use CSS pixels, not raw pixels
                // But actually, for canvas, it's easier to just draw in higher res coordinates 
                // OR style dimensions vs attribute dimensions.
                // Let's stick to attribute = CSS * DPR and logical drawing in CSS units?
                // No, simpler: 
                // Set canvas.width/height to true pixels.
                // But then we need to scale drawing ops.
                // Let's just reset the style width/height to confirm 100%.
                canvasRef.current.style.width = `${rect.width}px`;
                canvasRef.current.style.height = `${rect.height || 56}px`;
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <Box sx={{
            width: '100%',
            height: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f1f8e9',
            borderRadius: '24px',
            position: 'relative',
            overflow: 'hidden',
            px: 1,
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)' // Subtle inner shadow for depth
        }}>
            <canvas
                ref={canvasRef}
                style={{ width: '100%', height: '100%', display: 'block' }}
            />

            {/* Optional: Add a subtle 'REC' indicator or timerOverlay if requested, 
          but simpler is cleaner for now. */}
        </Box>
    );
};

export default AudioWaveform;
